import { motion } from "framer-motion";
import { Map, Building2, LayoutDashboard, Package, PersonStanding, MoveRight } from "lucide-react";

const WARM = "#C9A24B";
const GREEN = "#6B8F71";
const INK = "#1A1A1A";

const EASE = [0.22, 1, 0.36, 1];

const scales = [
  {
    key: "urban",
    label: "Urban",
    unit: "≈ km",
    icon: Map,
    color: WARM,
    body: "The city as a system: flows, density, how people move.",
  },
  {
    key: "buildings",
    label: "Buildings",
    unit: "≈ 100 m",
    icon: Building2,
    color: GREEN,
    body: "Structure and program: holding both life and logic.",
  },
  {
    key: "spaces",
    label: "Spaces",
    unit: "≈ 10 m",
    icon: LayoutDashboard,
    color: WARM,
    body: "Interiors: how a room shapes how you feel and work.",
  },
  {
    key: "products",
    label: "Products",
    unit: "≈ 10 cm",
    icon: Package,
    color: GREEN,
    body: "Objects and tools: the same instinct, in your hand.",
  },
  {
    key: "human",
    label: "Human",
    unit: "1:1",
    icon: PersonStanding,
    color: WARM,
    body: "The person it all serves: comfort, reach, how it feels.",
  },
];

// station x-centres inside the 1000-wide viewBox
const STATION_X = [120, 310, 500, 690, 880];
const BASE_Y = 46;

const minorTicks = [];
for (let x = 60; x <= 940; x += 20) minorTicks.push(x);

export const ScaleRuler = () => {
  return (
    <motion.div
      data-testid="scale-ruler"
      className="relative w-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.9, ease: EASE }}
    >
      <div className="overflow-x-auto pb-2">
        <div className="relative min-w-[900px]">
          {/* station cards (label + icon) sitting above the ruler */}
          <div className="relative h-[132px]">
            {scales.map((s, i) => {
              const Icon = s.icon;
              const leftPct = (STATION_X[i] / 1000) * 100;
              return (
                <motion.div
                  key={s.key}
                  data-testid={`scale-station-${s.key}`}
                  className="absolute bottom-0 -translate-x-1/2 flex flex-col items-center text-center w-40"
                  style={{ left: `${leftPct}%` }}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 + i * 0.14, ease: EASE }}
                >
                  <div
                    className="flex items-center justify-center rounded-full border bg-white transition-transform duration-500 hover:scale-105"
                    style={{
                      borderColor: s.color,
                      color: s.color,
                      width: 52,
                      height: 52,
                    }}
                  >
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <p className="font-serif text-lg text-ink mt-3 leading-none">{s.label}</p>
                  <p className="font-sans text-[11px] text-ink/60 leading-snug mt-1.5 px-1">
                    {s.body}
                  </p>
                  {s.note && (
                    <span
                      className="mt-2 font-mono text-[9px] tracking-[0.12em] uppercase rounded-full border px-2 py-0.5"
                      style={{ borderColor: s.color, color: s.color }}
                    >
                      {s.note}
                    </span>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* the ruler itself */}
          <svg viewBox="0 0 1000 72" className="w-full h-auto" aria-hidden="true">
            {/* baseline */}
            <line x1="50" y1={BASE_Y} x2="950" y2={BASE_Y} stroke={INK} strokeWidth="1" opacity="0.35" />

            {/* minor graduations */}
            {minorTicks.map((x) => (
              <line
                key={x}
                x1={x}
                y1={BASE_Y}
                x2={x}
                y2={BASE_Y - 6}
                stroke={INK}
                strokeWidth="0.6"
                opacity="0.3"
              />
            ))}

            {/* major station ticks + unit labels */}
            {scales.map((s, i) => {
              const x = STATION_X[i];
              return (
                <g key={s.key}>
                  <motion.line
                    x1={x}
                    y1={BASE_Y}
                    x2={x}
                    y2={BASE_Y - 16}
                    stroke={s.color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 + i * 0.14, ease: EASE }}
                  />
                  <circle cx={x} cy={BASE_Y} r="3.4" fill={s.color} />
                  <text
                    x={x}
                    y={BASE_Y + 18}
                    textAnchor="middle"
                    className="font-mono"
                    style={{ fontSize: "11px", letterSpacing: "0.14em", fill: s.color }}
                  >
                    {s.unit}
                  </text>
                </g>
              );
            })}

            {/* zoom-in arrow at the far right */}
            <line x1="950" y1={BASE_Y} x2="970" y2={BASE_Y} stroke={INK} strokeWidth="1" opacity="0.35" />
            <polyline
              points={`963,${BASE_Y - 4} 970,${BASE_Y} 963,${BASE_Y + 4}`}
              fill="none"
              stroke={INK}
              strokeWidth="1"
              opacity="0.35"
            />
          </svg>
        </div>
      </div>

      <p className="flex items-center gap-2 justify-center font-mono text-[10px] tracking-[0.24em] uppercase text-[#C9A24B] mt-4">
        Zooming in: each scale informs the next, from the city to the human
        <MoveRight size={13} strokeWidth={1.5} />
      </p>
    </motion.div>
  );
};

export default ScaleRuler;
